/**
 * App-wide passive staleness indicator (ISS-445 / #4602).
 *
 * THE GAP THIS CLOSES. `getDocsEarned` has three outcomes, and only two of them
 * are visible anywhere:
 *
 *   fromCache + empty     -> throws DegradedReadError  -> DegradedReadState (visible)
 *   refetch error + data  -> query error with data     -> StaleDataBanner    (visible)
 *   fromCache + HAS data  -> RESOLVES NORMALLY         -> nothing at all     <- this
 *
 * The third is the silent cache fallback: the read succeeds, the caller gets real
 * rows, and no query anywhere carries a staleness marker — so a technician can work
 * for minutes against cached data believing it is live. The degradation IS recorded,
 * but only in the shared reachability monitor, which no persistent surface watched
 * until this component existed.
 *
 * Deliberately layout-level and mounted ONCE (ModuleLayout), not per page: the
 * condition is a property of the CONNECTION, not of any one query, and making every
 * surface subscribe would be both duplicated work and easy to forget on the next
 * page someone adds.
 *
 * Renders nothing while online — a persistent "you are connected" chip is noise, and
 * noise is what gets ignored when it finally matters.
 */
import { CloudOff, WifiOff } from 'lucide-react';
import { useReachability } from '@officehub/offline-sdk';
import { reachability } from '../../lib/reachability';

export function ConnectionStatusBadge() {
  const state = useReachability(reachability);

  if (state === 'online') return null;

  const offline = state === 'offline';

  return (
    <div
      role="status"
      aria-live="polite"
      className={
        'flex items-center justify-center gap-2 px-4 py-1.5 text-xs font-medium ' +
        (offline
          ? 'bg-slate-800 text-slate-100'
          : 'bg-amber-50 text-amber-800 border-b border-amber-200')
      }
    >
      {offline ? (
        <WifiOff className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
      ) : (
        <CloudOff className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
      )}
      <span>
        {offline
          ? "You're offline — anything on screen may be out of date."
          : 'Weak connection — some of what you see may be out of date.'}
      </span>
    </div>
  );
}
